// ConversationsManager — ChatWidget transcripts viewer.
import React, { useCallback, useEffect, useState } from 'react';
import { Bot, Loader2, RefreshCw, Search, Send, Trash2, UserCheck } from 'lucide-react';
import { apiCall } from './_shared';
import CannedReplies from './CannedReplies';
import { PresenceLine, WhatsAppBridge, usePresence, useStaffTypingPing } from './ChatPresence';
import TranscriptEmail from './TranscriptEmail';
import OfferToBooking from './OfferToBooking';
import { TeamScores, useTeamScores } from './TeamScores';
import { Flag } from 'lucide-react';
import { useChatChime, useTitleFlash } from './chatAlerts';

const fmt = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const ConversationsManager = ({ userRole }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [searchQ, setSearchQ] = useState('');

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiCall(`/api/admin/chat-sessions?days=${days}&limit=200`);
      setSessions(data.sessions || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const [meta, setMeta] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const [unanswered, setUnanswered] = useState([]);
  const [translate, setTranslate] = useState(true);
  const [replyWarning, setReplyWarning] = useState('');
  const [glossing, setGlossing] = useState(false);
  const presence = usePresence(selected);
  const scores = useTeamScores(30);
  const endChat = async () => {
    if (!selected || !window.confirm('End this chat? The AI takes over again and the guest is asked to rate our reception.')) return;
    try { await apiCall(`/api/admin/chat-sessions/${selected}/end`, { method: 'POST' }); const d = await apiCall(`/api/admin/chat-sessions/${selected}`); setTranscript(d.messages || []); setMeta(d.meta || null); } catch (e) { setError(e.message); }
  };
  const { enabled: soundOn, toggle: toggleSound } = useChatChime(unanswered);
  useTitleFlash(unanswered.length);
  const pingTyping = useStaffTypingPing(selected);
  const guestLang = (meta?.guest_lang || 'en').toLowerCase();
  const LANG_NAMES = { en: 'English', me: 'Montenegrin', sr: 'Serbian', sq: 'Albanian', de: 'German', ru: 'Russian', pl: 'Polish', hu: 'Hungarian', it: 'Italian', fr: 'French' };
  const glossSession = async () => {
    if (!selected) return;
    setGlossing(true);
    try {
      await apiCall(`/api/admin/chat-sessions/${selected}/gloss`, { method: 'POST' });
      const d = await apiCall(`/api/admin/chat-sessions/${selected}`);
      setTranscript(d.messages || []); setMeta(d.meta || null);
    } catch (e) { setError(e.message); } finally { setGlossing(false); }
  };
  useEffect(() => {
    const tick = () => apiCall('/api/admin/chat-live/unanswered').then((d) => setUnanswered(d.sessions || [])).catch(() => {});
    tick();
    const id = setInterval(tick, 15000);
    return () => clearInterval(id);
  }, []);
  // Auto-refresh the open transcript so new guest lines appear while staff is replying.
  useEffect(() => {
    if (!selected) return undefined;
    const id = setInterval(() => {
      apiCall(`/api/admin/chat-sessions/${selected}`).then((d) => { setTranscript(d.messages || []); setMeta(d.meta || null); }).catch(() => {});
    }, 4000);
    return () => clearInterval(id);
  }, [selected]);
  const sendReply = async (textOverride) => {
    const text = (typeof textOverride === 'string' ? textOverride : reply).trim();
    if (!text || !selected) return;
    setSending(true);
    setReplyWarning('');
    try {
      const r = await apiCall(`/api/admin/chat-sessions/${selected}/reply`, { method: 'POST', body: JSON.stringify({ text, translate }) });
      if (r.warning) setReplyWarning(r.warning);
      setReply('');
      const d = await apiCall(`/api/admin/chat-sessions/${selected}`);
      setTranscript(d.messages || []); setMeta(d.meta || null);
      setUnanswered((u) => u.filter((x) => x.session_id !== selected));
    } catch (e) { setError(e.message); } finally { setSending(false); }
  };
  const setMode = async (mode) => {
    try { setMeta(await apiCall(`/api/admin/chat-sessions/${selected}/mode`, { method: 'POST', body: JSON.stringify({ mode }) })); } catch (e) { setError(e.message); }
  };
  const openSession = async (sid) => {
    setSelected(sid);
    setTranscriptLoading(true);
    setTranscript([]);
    try {
      const data = await apiCall(`/api/admin/chat-sessions/${sid}`);
      setTranscript(data.messages || []);
      setMeta(data.meta || null);
    } catch (err) {
      setTranscript([]);
      setError(err.message);
    } finally {
      setTranscriptLoading(false);
    }
  };

  const deleteSession = async (sid) => {
    if (!window.confirm('Permanently delete this conversation? This cannot be undone.')) return;
    try {
      await apiCall(`/api/admin/chat-sessions/${sid}`, { method: 'DELETE' });
      setSelected(null);
      setTranscript([]);
      fetchSessions();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  const visibleSessions = searchQ.trim()
    ? sessions.filter(s => {
        const q = searchQ.toLowerCase();
        return (s.first_user_message || '').toLowerCase().includes(q)
          || (s.last_user_message || '').toLowerCase().includes(q)
          || (s.session_id || '').toLowerCase().includes(q);
      })
    : sessions;

  const unansweredIds = new Set(unanswered.map((u) => u.session_id));
  // Rows precomputed OUTSIDE JSX (Babel workaround).
  const sessionList = visibleSessions.map(s => (
    <button
      key={s.session_id}
      onClick={() => openSession(s.session_id)}
      className={`w-full text-left px-4 py-3 border-b hover:bg-orange-50 transition-colors ${selected === s.session_id ? 'bg-orange-50' : ''}`}
      data-testid={`conversation-item-${s.session_id}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-mono text-gray-400 truncate">{s.session_id.slice(0, 8)}…{unansweredIds.has(s.session_id) && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold not-italic" data-testid={`conversation-unanswered-${s.session_id}`}>waiting</span>}</span>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{s.message_count} msg</span>
      </div>
      <p className="text-sm font-medium text-gray-800 mt-1 line-clamp-2">
        {s.first_user_message || '(no user message)'}
      </p>
      <p className="text-xs text-gray-400 mt-1">{fmt(s.last_at)}</p>
    </button>
  ));

  const transcriptRows = transcript.map((m, idx) => {
    if (m.staff_message && (m.channel === 'email' || m.channel === 'whatsapp' || m.channel === 'system')) {
      return <p key={idx} className="text-[11px] text-slate-400 text-center italic" data-testid={`transcript-system-${idx}`}>{m.staff_message} · {m.staff} · {fmt(m.timestamp)}</p>;
    }
    if (m.staff_message) {
      return (
        <div key={idx} className="space-y-1" data-testid={`transcript-staff-${idx}`}>
          <div className="flex justify-start">
            <div className="max-w-[85%] bg-green-600 text-white rounded-2xl rounded-bl-md px-4 py-2 text-sm whitespace-pre-wrap shadow-sm">
              <p className="text-[10px] uppercase font-bold opacity-80">Reception · {m.staff}{m.translated_to ? ` · translated → ${m.translated_to.toUpperCase()}` : ''}</p>{m.staff_message}
              {m.staff_original && <p className="mt-1 pt-1 border-t border-white/30 text-[11px] opacity-80 italic" data-testid={`transcript-staff-${idx}-original`}>EN: {m.staff_original}</p>}
            </div>
          </div>
          <p className="text-[11px] text-gray-400 text-center">{fmt(m.timestamp)}</p>
        </div>
      );
    }
    return (
      <div key={idx} className="space-y-2">
        <div className="flex justify-end">
          <div className="max-w-[85%] bg-orange-500 text-white rounded-2xl rounded-br-md px-4 py-2 text-sm whitespace-pre-wrap">
            {m.user_message}
            {m.user_gloss && <p className="mt-1 pt-1 border-t border-white/30 text-[11px] opacity-90 italic" data-testid={`transcript-gloss-${idx}`}>EN: {m.user_gloss}</p>}
          </div>
        </div>
        {m.bot_response ? (
          <div className="flex justify-start">
            <div className="max-w-[85%] bg-white border rounded-2xl rounded-bl-md px-4 py-2 text-sm text-gray-800 whitespace-pre-wrap shadow-sm">
              {m.bot_response}
            </div>
          </div>
        ) : <p className="text-[11px] text-red-500 text-center font-semibold" data-testid={`transcript-unanswered-${idx}`}>Waiting for reception — no AI answer (human mode)</p>}
        <p className="text-[11px] text-gray-400 text-center">{fmt(m.timestamp)}</p>
      </div>
    );
  });

  return (
    <div className="space-y-4" data-testid="conversations-manager">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">Conversations {unanswered.length > 0 && <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-xs font-bold" data-testid="conversations-unanswered-badge">{unanswered.length} waiting</span>}
          <button type="button" onClick={toggleSound} className={`text-xs font-semibold rounded-full px-2 py-0.5 border ${soundOn ? 'bg-emerald-50 text-emerald-800 border-emerald-300' : 'bg-gray-100 text-gray-500 border-gray-300'}`} title="Soft chime + tab flash when a guest writes during a takeover" data-testid="conversations-sound-toggle">{soundOn ? '🔔 chime on' : '🔕 chime off'}</button>
        </h2>
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-sm text-gray-500">Last</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="px-3 py-1.5 border rounded-lg text-sm"
            data-testid="conversations-days-select"
          >
            <option value={7}>7 days</option>
            <option value={30}>30 days</option>
            <option value={90}>90 days</option>
            <option value={365}>1 year</option>
          </select>
          <div className="relative">
            <Search className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              placeholder="Search messages..."
              className="pl-8 pr-3 py-1.5 border rounded-lg text-sm w-48"
              data-testid="conversations-search"
            />
          </div>
          <button
            onClick={fetchSessions}
            className="flex items-center gap-2 px-4 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 text-sm"
            data-testid="conversations-refresh"
          >
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>
      </div>

      <TeamScores data={scores} />
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
      )}

      <div className="grid md:grid-cols-[380px_1fr] gap-4">
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden max-h-[70vh] flex flex-col">
          <div className="px-4 py-2 border-b text-sm text-gray-500 flex items-center justify-between">
            <span>{visibleSessions.length} session{visibleSessions.length === 1 ? '' : 's'}</span>
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
          </div>
          <div className="overflow-y-auto flex-1">
            {!loading && visibleSessions.length === 0 && (
              <p className="p-6 text-center text-sm text-gray-400">No conversations in this window.</p>
            )}
            {sessionList}
          </div>
        </div>

        <div className="bg-white rounded-xl border shadow-sm overflow-hidden max-h-[70vh] flex flex-col">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">
                {selected ? 'Transcript' : 'Select a conversation'}
              </p>
              {selected && <p className="text-xs font-mono text-gray-400 truncate">{selected}</p>}
            </div>
            {selected && userRole === 'super_admin' && (
              <button
                onClick={() => deleteSession(selected)}
                className="flex items-center gap-1 text-xs text-red-600 hover:bg-red-50 px-2 py-1 rounded"
                data-testid="conversation-delete"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1 p-4 space-y-4 bg-gray-50">
            {!selected && (
              <p className="text-sm text-gray-400 text-center mt-10">Pick a session from the left to read the full conversation.</p>
            )}
            {selected && transcriptLoading && (
              <div className="flex items-center justify-center p-8"><Loader2 className="w-5 h-5 animate-spin text-orange-500" /></div>
            )}
            {selected && !transcriptLoading && transcript.length === 0 && (
              <p className="text-sm text-gray-400 text-center mt-10">No messages in this session.</p>
            )}
            {transcriptRows}
          </div>
          {selected && (
            <div className="border-t p-3 bg-white space-y-2" data-testid="conversation-reply-box">
              <div className="flex items-center justify-between text-xs flex-wrap gap-2">
                <span className={`px-2 py-0.5 rounded-full font-semibold ${meta?.mode === 'human' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`} data-testid="conversation-mode">{meta?.mode === 'human' ? 'Human mode — AI paused, you are answering' : 'AI mode — bot is answering'}</span>
                <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-800 font-semibold" data-testid="conversation-guest-lang">Guest language: {LANG_NAMES[guestLang] || guestLang.toUpperCase()}</span>
                {guestLang !== 'en' && (
                  <>
                    <label className="inline-flex items-center gap-1 text-slate-700 cursor-pointer" data-testid="conversation-translate-toggle">
                      <input type="checkbox" checked={translate} onChange={(e) => setTranslate(e.target.checked)} /> Auto-translate my reply → {LANG_NAMES[guestLang] || guestLang.toUpperCase()}
                    </label>
                    <button type="button" onClick={glossSession} disabled={glossing} className="text-sky-700 hover:text-sky-900 disabled:opacity-50" data-testid="conversation-gloss">{glossing ? 'Translating…' : 'Show guest lines in English'}</button>
                  </>
                )}
                {meta?.guest_email || meta?.guest_phone ? <span className="text-gray-500">{meta.guest_name ? `${meta.guest_name} · ` : ''}{meta.guest_email || meta.guest_phone}</span> : null}
                {meta?.rating && <span className={`px-2 py-0.5 rounded-full font-semibold ${meta.rating === 'up' ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`} data-testid="conversation-rating">{meta.rating === 'up' ? '👍 guest was happy' : '👎 guest unhappy'}{meta.rating_comment ? ` · “${meta.rating_comment}”` : ''}</span>}
                {meta?.mode === 'human' && !meta?.ended_at && <button type="button" onClick={endChat} className="inline-flex items-center gap-1 text-red-700 hover:text-red-900 font-semibold" data-testid="conversation-end-chat"><Flag className="w-3.5 h-3.5" /> End chat</button>}
                {meta?.mode === 'human' ? (
                  <button type="button" onClick={() => setMode('ai')} className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900" data-testid="conversation-mode-ai"><Bot className="w-3.5 h-3.5" /> Hand back to AI</button>
                ) : (
                  <button type="button" onClick={() => setMode('human')} className="inline-flex items-center gap-1 text-green-700 hover:text-green-900" data-testid="conversation-mode-human"><UserCheck className="w-3.5 h-3.5" /> Take over</button>
                )}
              </div>
              <CannedReplies userRole={userRole} onPick={(text) => setReply(text)} onSend={(text) => sendReply(text)} />
              <OfferToBooking sessionId={selected} guestName={meta?.guest_name} guestEmail={meta?.guest_email} guestPhone={meta?.guest_phone} onDone={() => apiCall(`/api/admin/chat-sessions/${selected}`).then((d) => { setTranscript(d.messages || []); setMeta(d.meta || null); }).catch(() => {})} />
              <div className="flex gap-2">
                <textarea value={reply} onChange={(e) => { setReply(e.target.value); pingTyping(); }} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendReply(); } }} rows={2} placeholder="Reply to the guest as Reception… (Enter to send)" className="flex-1 border rounded-lg px-3 py-2 text-sm" data-testid="conversation-reply-input" />
                <button type="button" onClick={sendReply} disabled={sending || !reply.trim()} className="px-4 rounded-lg bg-green-600 hover:bg-green-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-1" data-testid="conversation-reply-send">{sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send</button>
              </div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <PresenceLine presence={presence} />
                <div className="flex items-start gap-2 flex-wrap justify-end">
                  <TranscriptEmail sessionId={selected} guestEmail={meta?.guest_email} draft={reply} onDone={() => apiCall(`/api/admin/chat-sessions/${selected}`).then((d) => { setTranscript(d.messages || []); setMeta(d.meta || null); }).catch(() => {})} />
                  <WhatsAppBridge sessionId={selected} presence={presence} draft={reply} onDone={() => { setReply(''); apiCall(`/api/admin/chat-sessions/${selected}`).then((d) => { setTranscript(d.messages || []); setMeta(d.meta || null); }).catch(() => {}); }} />
                </div>
              </div>
              {replyWarning && <p className="text-[11px] text-amber-700 font-semibold" data-testid="conversation-reply-warning">{replyWarning}</p>}
              <p className="text-[11px] text-gray-400">Sending a reply pauses the AI for this guest; they see your answer within 5 seconds as "Reception".{guestLang !== 'en' && translate ? ' Your English is translated automatically.' : ''}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationsManager;
